import { useState, useEffect, useMemo } from 'react'
import { bancosApi, TIPOVALOR, TIPOVALOR_LABEL } from '../api/bancos'

/**
 * Maestro de formas de pago vigentes en Presea (tabla BANCOS_LGR.DBI). Solo
 * lectura — Presea es la única fuente y se sincroniza cada noche por el cron
 * `presea:sincronizar-maestros`. Se usa para que el operador del recibo elija
 * el N_BCO real (tarjeta→marca, cheque→banco, efectivo→caja, transferencia→cuenta).
 */
const TABS = [
  { tipo: 'TODOS',                 label: 'Todos',              icon: '📋' },
  { tipo: TIPOVALOR.TARJETA,       label: 'Tarjeta / Pasarela', icon: '💳' },
  { tipo: TIPOVALOR.EFECTIVO,      label: 'Efectivo',           icon: '💵' },
  { tipo: TIPOVALOR.TRANSFERENCIA, label: 'Transferencia',      icon: '🏦' },
  { tipo: TIPOVALOR.CHEQUE,        label: 'Cheque',             icon: '📄' },
  { tipo: 'OTROS',                 label: 'Otros',              icon: '⋯' },
]

const TIPOS_PRINCIPALES = new Set([TIPOVALOR.TARJETA, TIPOVALOR.EFECTIVO, TIPOVALOR.TRANSFERENCIA, TIPOVALOR.CHEQUE])

export default function BancosPreseaPage() {
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Por defecto "Todos" para que se vea el JSON completo cargado, sin filtros.
  const [tab, setTab] = useState('TODOS')
  const [q, setQ] = useState('')

  useEffect(() => {
    setLoading(true); setError('')
    // limit=500 cubre las ~305 filas actuales con margen; si Presea agregara más,
    // subir acá. Pedimos el maestro completo para garantizar que cada item del
    // JSON se vea en la página (no paginamos del lado servidor por simpleza).
    bancosApi.list({ limit: 500 })
      .then(({ data }) => setFilas(data || []))
      .catch(e => setError(e.response?.data?.message || 'No se pudo cargar el maestro.'))
      .finally(() => setLoading(false))
  }, [])

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase()
    return filas.filter(b => {
      if (tab === 'TODOS') {
        // sin filtro de tipo: cada item del JSON pasa
      } else if (tab === 'OTROS') {
        if (TIPOS_PRINCIPALES.has(b.tipovalor)) return false
      } else if (b.tipovalor !== tab) {
        return false
      }
      if (!term) return true
      return (b.nombre || '').toLowerCase().includes(term) || String(b.n_bco ?? '').includes(term)
    })
  }, [filas, tab, q])

  const conteo = useMemo(() => {
    const c = { TODOS: filas.length, OTROS: 0 }
    TABS.forEach(t => { if (typeof t.tipo === 'number') c[t.tipo] = 0 })
    filas.forEach(b => {
      if (TIPOS_PRINCIPALES.has(b.tipovalor)) c[b.tipovalor] = (c[b.tipovalor] || 0) + 1
      else c.OTROS++
    })
    return c
  }, [filas])

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <h2 style={st.title}>Formas de pago vigentes en Presea</h2>
      <p style={st.sub}>
        Maestro <code>BANCOS</code> de Presea (sincronizado cada noche por el cron de maestros). Es la
        lista oficial de N_BCO que Presea acepta en el recibo: <b>tarjeta</b> → marca,
        <b> cheque</b> → banco emisor, <b>efectivo/transferencia</b> → la caja o cuenta concreta.
        Solo lectura — para dar de alta o modificar, hay que hacerlo en Presea.
      </p>

      <div style={st.tabs}>
        {TABS.map(t => (
          <button
            key={t.tipo}
            onClick={() => setTab(t.tipo)}
            style={{ ...st.tab, ...(tab === t.tipo ? st.tabActive : null) }}
          >
            <span style={{ marginRight: 6 }}>{t.icon}</span>
            {t.label}
            <span style={st.tabCount}>{conteo[t.tipo] ?? 0}</span>
          </button>
        ))}
      </div>

      <input
        type="search"
        placeholder="Buscar por nombre o N_BCO…"
        value={q}
        onChange={e => setQ(e.target.value)}
        style={st.search}
      />

      {loading ? (
        <p style={st.muted}>Cargando…</p>
      ) : error ? (
        <p style={st.errorMsg}>{error}</p>
      ) : filtradas.length === 0 ? (
        <p style={st.muted}>Sin resultados.</p>
      ) : (
        <table style={st.tabla}>
          <thead>
            <tr>
              <th style={{ ...st.th, width: 110 }}>N_BCO</th>
              <th style={st.th}>Nombre</th>
              <th style={st.th}>Cuenta</th>
              <th style={{ ...st.th, width: 100 }}>Tipo</th>
            </tr>
          </thead>
          <tbody>
            {filtradas.map(b => (
              <tr key={b.n_bco ?? `${b.tipovalor}-${b.nombre}`} style={st.tr}>
                <td style={st.td}><code style={st.code}>{b.n_bco ?? '—'}</code></td>
                <td style={st.td}>{b.nombre || <span style={{ color: '#cbd5e1' }}>(sin nombre)</span>}</td>
                <td style={{ ...st.td, color: '#64748b' }}>{b.cuenta || '—'}</td>
                <td style={st.td}>
                  <span style={st.pill}>{TIPOVALOR_LABEL[b.tipovalor] || (b.tipovalor != null ? `#${b.tipovalor}` : 'sin tipo')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const st = {
  title:     { margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' },
  sub:       { margin: '0 0 1.25rem', color: '#64748b', fontSize: '0.875rem', lineHeight: 1.5 },
  tabs:      { display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  tab:       { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.45rem 0.85rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem', color: '#475569', fontWeight: 600 },
  tabActive: { background: '#1e293b', borderColor: '#1e293b', color: 'white' },
  tabCount:  { marginLeft: 6, padding: '1px 6px', borderRadius: 10, background: 'rgba(148,163,184,0.25)', fontSize: '0.72rem', fontWeight: 700 },
  search:    { width: '100%', maxWidth: 320, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', marginBottom: '0.75rem' },
  tabla:     { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', fontSize: '0.875rem' },
  th:        { padding: '0.6rem 0.75rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' },
  tr:        { borderBottom: '1px solid #f1f5f9' },
  td:        { padding: '0.55rem 0.75rem', verticalAlign: 'middle' },
  code:      { fontFamily: 'monospace', fontSize: '0.8rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#1e293b' },
  pill:      { fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 10, background: '#e0e7ff', color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.02em' },
  muted:     { color: '#94a3b8', fontSize: '0.875rem' },
  errorMsg:  { color: '#dc2626', fontSize: '0.875rem' },
}
